import { createSelector, createSlice } from '@reduxjs/toolkit';

// The backend Settings collection is a singleton. It stores canonical DB field
// names (e.g. `hour24PayAvailableBadge`, `enableReferralBanner`) and a
// `showReferencesViewFor` array of city strings. This maps that raw document to
// the flat, UI-friendly shape the Settings screen renders.
export const mapSettingsDocToUi = (doc = {}) => {
    const cities = Array.isArray(doc.showReferencesViewFor)
        ? doc.showReferencesViewFor
        : [];

    return {
        _id: doc._id || null,
        enablePayBadge: !!doc.hour24PayAvailableBadge,
        enableResponseTime: !!doc.enableResponseTime,
        enableAcceptanceRate: !!doc.enableAcceptanceRate,
        enableRatingsPopupInApp: !!doc.enableRatingsPopupInApp,
        enableLocBaseShiftStart: !!doc.enableLocBaseShiftStart,
        enableVssAttestation: !!doc.enableVssAttestation,
        enableReferralBannerPopup: !!doc.enableReferralBanner,
        enableAppRating: !!doc.enableAppRating,
        enableMailingAddressInApp: !!doc.enableMailingAddressInApp,
        // In-app support chat: who gets the chat button. Flattened so each
        // switch saves through saveField like any other toggle.
        supportChatStaffersApproved: !!(doc.supportChat || {}).staffersApproved,
        supportChatStaffersOnboarding: !!(doc.supportChat || {})
            .staffersOnboarding,
        supportChatClientsApproved: !!(doc.supportChat || {}).clientsApproved,
        supportChatClientsOnboarding: !!(doc.supportChat || {})
            .clientsOnboarding,
        enableDisableOnboarding: Array.isArray(doc.enableDisableOnboarding)
            ? doc.enableDisableOnboarding
            : [],
        referencesView: {
            toronto: cities.includes('Toronto'),
            vancouver: cities.includes('Vancouver'),
            newyork: cities.includes('New_York'),
        },
        messageOfDay: doc.messageOfDay || '',
        messageOfDayInFrench: doc.messageOfDayInFrench || '',
    };
};

const initialState = {
    data: null,
    isLoading: false,
    error: null,
    // Key of the toggle/field currently being persisted — powers the subtle
    // inline "saving" spinner. Booleans use the UI field key (e.g.
    // 'enableVssAttestation'); references-view uses 'ref:<cityKey>'.
    savingKey: null,
    isSavingMessage: false,
    isTestingPayment: false,
    // Snapshots of the pre-change value, kept only while a save is in flight so
    // an optimistic update can be rolled back if the request fails.
    pendingField: {},
    pendingRef: {},
};

const slice = createSlice({
    name: 'platformSettings',
    initialState,
    reducers: {
        fetchSettings: state => {
            state.isLoading = true;
            state.error = null;
        },
        settingsFetched: (state, { payload }) => {
            state.data = payload;
            state.isLoading = false;
        },
        settingsFetchFailed: (state, { payload }) => {
            state.isLoading = false;
            state.error = payload || 'Failed to load settings';
        },

        // ── Boolean toggles + onboarding-regions array ──
        // Optimistic: apply the change immediately so the UI is instant, and
        // stash the previous value in case we need to roll back.
        saveField: (state, { payload }) => {
            const { key, value } = payload;
            if (state.data) {
                state.pendingField[key] = state.data[key];
                state.data[key] = value;
            }
            state.savingKey = key;
        },
        saveFieldSucceeded: (state, { payload }) => {
            delete state.pendingField[payload.key];
            state.savingKey = null;
        },
        saveFieldFailed: (state, { payload }) => {
            const { key } = payload;
            if (state.data && key in state.pendingField) {
                state.data[key] = state.pendingField[key];
            }
            delete state.pendingField[key];
            state.savingKey = null;
        },

        // ── References-view city toggles (also optimistic) ──
        saveReferencesView: (state, { payload }) => {
            const { cityKey, value } = payload;
            if (state.data) {
                state.pendingRef[cityKey] = state.data.referencesView[cityKey];
                state.data.referencesView[cityKey] = value;
            }
            state.savingKey = `ref:${cityKey}`;
        },
        saveReferencesViewSucceeded: (state, { payload }) => {
            delete state.pendingRef[payload.cityKey];
            state.savingKey = null;
        },
        saveReferencesViewFailed: (state, { payload }) => {
            const { cityKey } = payload;
            if (state.data && cityKey in state.pendingRef) {
                state.data.referencesView[cityKey] = state.pendingRef[cityKey];
            }
            delete state.pendingRef[cityKey];
            state.savingKey = null;
        },

        // ── Message of the day ──
        sendMessage: state => {
            state.isSavingMessage = true;
        },
        sendMessageSucceeded: (state, { payload }) => {
            state.isSavingMessage = false;
            if (state.data) {
                state.data.messageOfDay = payload.messageOfDay;
                state.data.messageOfDayInFrench = payload.messageOfDayInFrench;
            }
        },
        sendMessageFailed: state => {
            state.isSavingMessage = false;
        },

        // ── Diagnostics: pending-payment test ──
        testPendingPayment: state => {
            state.isTestingPayment = true;
        },
        testPendingPaymentSettled: state => {
            state.isTestingPayment = false;
        },
    },
});

export const {
    fetchSettings,
    settingsFetched,
    settingsFetchFailed,
    saveField,
    saveFieldSucceeded,
    saveFieldFailed,
    saveReferencesView,
    saveReferencesViewSucceeded,
    saveReferencesViewFailed,
    sendMessage,
    sendMessageSucceeded,
    sendMessageFailed,
    testPendingPayment,
    testPendingPaymentSettled,
} = slice.actions;

const getSelf = state => state.platformSettings;
export const selectPlatformSettings = createSelector(getSelf, s => s.data);
export const selectPlatformSettingsLoading = createSelector(
    getSelf,
    s => s.isLoading,
);
export const selectPlatformSettingsSavingKey = createSelector(
    getSelf,
    s => s.savingKey,
);
export const selectPlatformSettingsSavingMessage = createSelector(
    getSelf,
    s => s.isSavingMessage,
);
export const selectPlatformSettingsTestingPayment = createSelector(
    getSelf,
    s => s.isTestingPayment,
);

export default slice.reducer;
